import { useCallback, useMemo } from 'react'
import { PurchasesPackage } from 'react-native-purchases'

import type { PurchaseCurrencyCode } from '../utils/webBillingOfferings.types'

interface UsePurchasePricingParams {
  packages: PurchasesPackage[]
  language: string
  isSwedish: boolean
  targetCurrency: PurchaseCurrencyCode
}

interface UsePurchasePricingResult {
  formatCurrency: (value: number, currencyCode: string) => string
  yearlySavingsFormatted: string | null
}

export function usePurchasePricing({
  packages,
  language,
  isSwedish,
  targetCurrency,
}: UsePurchasePricingParams): UsePurchasePricingResult {
  const monthlyPackage = packages.find((pkg) => pkg.packageType === 'MONTHLY')
  const yearlyPackage = packages.find((pkg) => pkg.packageType === 'ANNUAL')
  const savingsCurrencyCode =
    yearlyPackage?.product.currencyCode ??
    monthlyPackage?.product.currencyCode ??
    targetCurrency

  const formatCurrency = useCallback(
    (value: number, currencyCode: string) => {
      if (isSwedish && currencyCode === 'SEK') {
        const formattedAmount = new Intl.NumberFormat('de-DE', {
          style: 'decimal',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
          useGrouping: true,
        }).format(value)
        return `${formattedAmount} kr`
      }

      return new Intl.NumberFormat(language, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: true,
      }).format(value)
    },
    [isSwedish, language],
  )

  const yearlySavingsFormatted = useMemo(() => {
    if (monthlyPackage == null || yearlyPackage == null) {
      return null
    }

    const savingsValue = Math.max(
      0,
      monthlyPackage.product.price * 12 - yearlyPackage.product.price,
    )
    return formatCurrency(savingsValue, savingsCurrencyCode)
  }, [formatCurrency, monthlyPackage, yearlyPackage, savingsCurrencyCode])

  return {
    formatCurrency,
    yearlySavingsFormatted,
  }
}
